<template>
    <div
        class="chat-message"
        :class="{
            'is-own': isOwnMessage,
            'is-other': !isOwnMessage,
            'is-sending': message.status === 'sending'
        }"
    >
        <!-- Avatar (for other's messages) -->
        <div v-if="!isOwnMessage && showAvatar" class="message-avatar">
            <div class="avatar-circle">
                {{ senderInitials }}
            </div>
        </div>

        <div class="message-content">
            <!-- Sender name (for other's messages) -->
            <div v-if="!isOwnMessage && showSenderName" class="sender-name">
                {{ senderName }}
            </div>

            <!-- Message bubble -->
            <div class="message-bubble">
                <!-- Text content -->
                <p v-if="message.type === 'text'" class="message-text">
                    {{ message.content }}
                </p>

                <!-- File/Image attachment -->
                <ChatFilePreview
                    v-if="message.attachment"
                    :attachment="message.attachment"
                />

                <!-- System message -->
                <p v-if="message.type === 'system'" class="system-message">
                    {{ message.content }}
                </p>
            </div>

            <!-- Message meta (time, status) -->
            <div class="message-meta">
                <span class="message-time">{{ formattedTime }}</span>
                <span v-if="isOwnMessage" class="message-status">
                    <i v-if="message.status === 'sending'" class="material-icons status-icon">schedule</i>
                    <i v-else-if="message.status === 'sent'" class="material-icons status-icon">done</i>
                    <i v-else-if="message.status === 'delivered'" class="material-icons status-icon">done_all</i>
                    <i v-else-if="message.status === 'read'" class="material-icons status-icon read">done_all</i>
                    <i v-else-if="message.status === 'failed'" class="material-icons status-icon failed">error</i>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import ChatFilePreview from './ChatFilePreview.vue';

export default {
    name: 'ChatMessage',
    components: {
        ChatFilePreview
    },
    props: {
        message: {
            type: Object,
            required: true
        },
        currentUserId: {
            type: String,
            required: true
        },
        showAvatar: {
            type: Boolean,
            default: true
        },
        showSenderName: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        isOwnMessage() {
            const sender = this.message.sender;
            const senderId = (sender && sender._id) || sender;
            return senderId === this.currentUserId || senderId === 'current';
        },
        senderName() {
            const sender = this.message.sender;
            return (sender && sender.fullName) || 'Utilisateur';
        },
        senderInitials() {
            const name = this.senderName;
            const parts = name.split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        formattedTime() {
            if (!this.message.createdAt) return '';

            const date = new Date(this.message.createdAt);
            const now = new Date();
            const isToday = date.toDateString() === now.toDateString();

            if (isToday) {
                return date.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }

            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
};
</script>

<style scoped>
.chat-message {
    display: flex;
    gap: 8px;
    padding: 4px 0;
    max-width: 85%;
}

.chat-message.is-own {
    flex-direction: row-reverse;
    margin-left: auto;
}

.chat-message.is-other {
    margin-right: auto;
}

.chat-message.is-sending {
    opacity: 0.7;
}

.message-avatar {
    flex-shrink: 0;
}

.avatar-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #059669;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
}

.message-content {
    display: flex;
    flex-direction: column;
}

.is-own .message-content {
    align-items: flex-end;
}

.is-other .message-content {
    align-items: flex-start;
}

.sender-name {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 2px;
    padding-left: 4px;
}

.message-bubble {
    padding: 10px 14px;
    border-radius: 16px;
    max-width: 100%;
    word-wrap: break-word;
}

.is-own .message-bubble {
    background-color: #059669;
    color: white;
    border-bottom-right-radius: 4px;
}

.is-other .message-bubble {
    background-color: #f3f4f6;
    color: #1f2937;
    border-bottom-left-radius: 4px;
}

.message-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
}

.system-message {
    margin: 0;
    font-size: 13px;
    font-style: italic;
    color: #6b7280;
    text-align: center;
}

.message-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    padding: 0 4px;
}

.message-time {
    font-size: 11px;
    color: #9ca3af;
}

.message-status {
    display: flex;
    align-items: center;
}

.status-icon {
    font-size: 14px;
    color: #9ca3af;
}

.status-icon.read {
    color: #3b82f6;
}

.status-icon.failed {
    color: #ef4444;
}
</style>
